import axios from "axios";
import API from "@/services/API.js";
import { apiFlagListTranslator } from "./flagTranslator.js";
import "./types.js";
import Vue from "vue";

/**
 * Feature Flag service for Vue.js 2
 * Mimics the Pinia store functionality from Nuxt 3
 */
class FlagService {
  constructor() {
    // Make the service reactive using Vue.observable
    this.state = Vue.observable({
      /** @type {DvFlags|null} */
      flags: null,
      /** @type {boolean} */
      isLoading: false,
      /** @type {boolean} */
      initialized: false,
    });

    /** @type {UniverseWithEmpty} */
    this.universe = "beepiz";

    // Test mode flag - remove this in production
    /** @type {boolean} */
    this.TEST = false;

    // Don't auto-fetch flags on initialization - wait for auth to complete
  }

  // Getter methods that access the reactive state
  get flags() {
    return this.state.flags;
  }

  get isLoading() {
    return this.state.isLoading;
  }

  get initialized() {
    return this.state.initialized;
  }

  /**
   * Initializes the flag service and fetches flags
   * Should be called after authentication is complete
   * @returns {Promise<void>}
   */
  async initialize() {
    if (!this.state.initialized) {
      this.state.initialized = true;
      await this.fetchFlags();
    }
  }

  /**
   * Fetches flags from the API
   * @returns {Promise<void>}
   */
  async fetchFlags() {
    if (this.state.isLoading === true) {
      return; // Prevent concurrent calls
    }

    this.state.isLoading = true;

    try {
      const endpoint = `${API.apiAuthBaseUrl}${API.endpoints.permissions.flag}/${this.universe}`;

      const response = await axios.get(endpoint);

      let data = response.data.data || response.data;

      this.state.flags = apiFlagListTranslator(data);
    } catch (error) {
      console.error("Error fetching flags:", error);
      this.state.flags = null;
    } finally {
      this.state.isLoading = false;
    }
  }

  /**
   * Refreshes the flags by fetching them again
   * @returns {Promise<void>}
   */
  async refreshFlags() {
    return this.fetchFlags();
  }

  /**
   * Gets a specific flag by key
   * @param {ApiFlagKey} flagKey - The flag key to retrieve
   * @returns {DvFlag|null}
   */
  getFlag(flagKey) {
    return this.state.flags?.[flagKey] || null;
  }

  /**
   * Checks if a flag has access
   * @param {ApiFlagKey} flagKey - The flag key to check
   * @returns {boolean}
   */
  hasAccess(flagKey) {
    const flag = this.getFlag(flagKey);
    return flag?.access ?? false;
  }

  /**
   * Gets all flags
   * @returns {DvFlags|null}
   */
  getAllFlags() {
    return this.state.flags;
  }

  /**
   * Checks if flags are currently loading
   * @returns {boolean}
   */
  getIsLoading() {
    return this.state.isLoading;
  }
}

// Create a singleton instance
const flagService = new FlagService();

export default flagService;
